import { useEffect, useState, useRef } from 'react';

import { Article as ArticleModel } from '@/constants';

import styles from './article.module.css';

import { ArticleContext, MAX_TEXT_COMPONENT } from './Article.context';

import { Canvas } from './Canvas';
import { StyledImage } from './components/Image';
import { StyledAdvert } from './components/Offer';
import { StyledSpacer } from './components/Spacer';
import { StyledVideo } from './components/Video';
import { StyledAudio } from './components/Audio';
import { StyledDivider } from './components/Divider';
import { StyledTwitterTimeline } from './components/Twitter';
import { StyledText } from './components/Text';
import { StyledInfogram } from './components/Infogram';
import { StyledColumns } from './components/Columns';
import { StyledGallery } from './components/Gallery';
import { StyledCustom } from './components/Custom';
import { StyledButton } from './components/Button';
import { Anchor } from './components/Anchor';
import { StyledTabs } from './components/Tabs';
import { StyledInstagram } from './components/Instagram';
import { StyledTikTok } from './components/TikTok';

import { CSS, DeviceType } from './CSS';
import { useArticleStyle } from './Article.hooks';

const textComponentsSet = new Set([
	'headline',
	'title',
	'subtitle',
	'intro',
	'body',
	'crosshead',
	'byline',
	'blockquote',
	'footer',
	'imagecaption',
]);

for (let i = 0; i <= MAX_TEXT_COMPONENT; i++) {
	textComponentsSet.add(`text${i + 1}`);
}

export const SingleArticle = (props: SingleArticleProps) => {
	const { article, language, device, fontSize } = props;
	const { id, style, components, ArticleID } = article;

	const ref = useRef<HTMLElement>(null);
	const [renderComponents, setRenderComponents] = useState<Array<any>>([]);
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);

	const { css, classNameMap } = useArticleStyle(article, props.styles);
	if (css) {
		console.log(css);
	}

	useEffect(() => {
		if (!css) return;
		if (!ref) {
			return;
		}

		const element = ref.current;
		if (!element) {
			return;
		}

		setWidth(element.clientWidth);
		setHeight(element.clientHeight);
	}, [ref, css, article]);

	useEffect(() => {
		if (!components) return;
		setRenderComponents(
			components
				.filter(filterByDevice(device))
				.map(mapByComponent(device))
				.filter((i: any) => !!i),
		);
	}, [device, components]);

	const className = [
		styles['article'],
		`article-${id}`,
		device !== 'mobile' ? device : '',
		classNameMap.get(`${article.id}`),
	];

	const isAd = components
		? components.filter(c => c.component === 'advert').length > 0
		: false;

	return (
		<ArticleContext.Provider
			value={{
				width,
				height,
				styles: props.styles,
				style: style ? clone(props.styles.get(style)) : null,
				lang: language ? language : 'en',
			}}
		>
			<article
				id={`article-${id}`}
				ref={ref}
				onScroll={e => {
					console.log(e);
				}}
				data-article-id={ArticleID}
				data-index={null}
				style={{
					fontSize: fontSize ? `${fontSize}%` : undefined,
					opacity: width ? 1 : 0,
					height: isAd ? '100%' : undefined,
				}}
				className={className.join(' ')}
			>
				<Canvas isAd={isAd}>{renderComponents}</Canvas>
				{css ? <style>{css}</style> : null}
			</article>
		</ArticleContext.Provider>
	);
};

interface SingleArticleProps {
	article: ArticleModel;
	styles: Map<string, any>;
	language: string;
	fontSize: number;
	/**
	 * This is the type of the device for the article
	 */
	device: DeviceType;
}

export const Article = (props: ArticleProps) => {
	const {
		id,
		components,
		ArticleId,
		lang,
		fontSize,
		style,
		index,
		device = 'desktop',
		isVoid,
		isTouchDevice,
	} = props;
	const ref = useRef<HTMLElement>(null);
	const [renderComponents, setRenderComponents] = useState<Array<any>>([]);
	const [css, setCSS] = useState('');
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		if (!ref) {
			return;
		}

		const element = ref.current;
		if (!element) {
			return;
		}

		setWidth(element.clientWidth);
		setHeight(element.clientHeight);

		const cssBuild = new CSS(id, props.styles, style, () => {});
		cssBuild.build().then((cssContent: any) => {
			if (isVideoAd(components)) {
				setCSS(
					[
						`
                    @media screen and (min-width: 768px) {
                        .article-${id} video {
                            height: calc(100vh - var(--navbar-height)) !important;
                        }
                    }
                    
                    @media screen and (max-width: 767px) {
                        .article-${id} > .canvas > :first-child,
                        .article-${id} .video,
                        .article-${id} .video a,
                        .article-${id} .video video {
                            height: 100%;
                        }
                    }`,
						cssContent,
					].join('\n'),
				);
			} else {
				setCSS(cssContent);
			}

			setTimeout(() => {
				setWidth(element.clientWidth);
			}, 500);
		});
	}, [ref, style, device, props.styles, id, components]);
	useEffect(() => {
		if (isVoid) {
			setRenderComponents([]);
			return;
		}
		setRenderComponents(
			components
				.filter(filterByDevice(device))
				.map(mapByComponent(device))
				.filter((i: any) => !!i),
		);
	}, [device, isVoid, components]);
	const className = [
		styles['article'],
		`article-${id}`,
		device !== 'mobile' ? device : '',
	];
	if (device === 'desktop' && !isTouchDevice) {
		className.push('swiper-no-swiping');
	}
	const isAd = components.filter(c => c.component === 'advert').length > 0;

	return (
		<ArticleContext.Provider
			value={{
				width,
				height,
				styles: props.styles,
				style: clone(props.styles.get(style)),
				lang: lang ? lang : 'en',
			}}
		>
			<article
				id={`article-${id}`}
				ref={ref}
				onScroll={e => {
					console.log(e);
				}}
				data-article-id={ArticleId}
				data-index={index ?? null}
				style={{
					fontSize: fontSize ? `${fontSize}%` : undefined,
					opacity: width ? 1 : 0,
					height: isAd ? '100%' : undefined,
				}}
				className={className.join(' ')}
			>
				<Canvas isAd={isAd}>{renderComponents}</Canvas>
				{css ? <style>{css}</style> : null}
			</article>
		</ArticleContext.Provider>
	);
};

interface ArticleProps {
	/**
	 * Is this the principal call to action on the page?
	 */
	id: string;
	/**
	 * This is the style that is going to be applied to the article
	 */
	style: string;
	/*
	 * Internal ID used by Canvasflow
	 */
	ArticleId?: string;
	index?: number;
	name?: any;
	lightbox?: boolean;
	styles: Map<string, any>;
	components: Array<any>;
	/**
	 * This is the language for the article
	 */
	lang: string;
	/**
	 * Font size that is applied to the article
	 */
	fontSize?: number;
	/**
	 * Signals if the components should be hidden
	 */
	isVoid: boolean;
	/**
	 * Determines if the article is in a touchable device
	 */
	isTouchDevice: boolean;
	onLightboxMode?: any;
	onLightboxTargetClick?: any;
	/**
	 * This is the type of the device for the article
	 */
	device?: DeviceType;
}

function isVideoAd(components: Array<any>) {
	const videoComponents = components.filter(c => c.component === 'video');
	return components.length === videoComponents.length;
}

function filterByDevice(device: string) {
	return ({ devices }: any) => {
		if (
			(devices.phone === 'off' && device === 'mobile') ||
			(devices.tablet === 'off' && device === 'tablet') ||
			(devices.desktop === 'off' && device === 'desktop')
		) {
			return false;
		}

		return true;
	};
}

function mapByComponent(device: DeviceType) {
	return (component: any) => {
		const key = component.id;
		if (key === 'Cf2-1141226346') {
			console.log(component);
		}
		if (textComponentsSet.has(component.component)) {
			return <StyledText key={key} {...component} />;
		}
		switch (component.component) {
			case 'image':
			case 'anscene':
				return <StyledImage key={key} {...component} />;
			case 'tabs':
				return <StyledTabs key={key} {...component} />;
			case 'advert':
				return <StyledAdvert key={key} {...component} />;
			case 'infogram':
				return <StyledInfogram key={key} {...component} />;
			case 'map':
				return null;
			case 'spacer':
				return <StyledSpacer key={key} {...component} />;
			case 'button':
				return <StyledButton key={key} {...component} />;
			case 'anchor':
				return <Anchor key={key} {...component} />;
			case 'audio':
				return <StyledAudio key={key} {...component} />;
			case 'video':
				return <StyledVideo key={key} {...component} />;
			case 'twitter':
				return <StyledTwitterTimeline key={key} {...component} />;
			case 'divider':
				return <StyledDivider key={key} {...component} />;
			case 'instagram':
				return <StyledInstagram key={key} {...component} />;
			case 'tiktok':
				return <StyledTikTok key={key} {...component} />;
			case 'columns':
				return (
					<StyledColumns
						device={device}
						key={component.id}
						{...component}
					/>
				);
			case 'gallery':
				if (component['control-speed']) {
					component.control_speed = component['control-speed'];
				}
				return <StyledGallery key={key} {...component} />;
			case 'custom':
				return <StyledCustom key={key} {...component} />;
			default:
				return null;
		}
	};
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
